import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, User, Menu, X, ChevronRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const { t } = useT();
  const [mobile, setMobile] = React.useState(false);

  // Lock body scroll when mobile menu is open (iOS-safe)
  React.useEffect(() => {
    if (!mobile) return;
    const scrollY = window.scrollY;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [mobile]);

  return (
    <>
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-gray-200" data-testid="site-header">
      {/* Announcement bar */}
      <div className="bg-gray-900 text-white py-2 px-6 md:px-10 text-center text-[11px] md:text-xs uppercase tracking-[0.2em] font-semibold" data-testid="announcement-bar">
        <span className="inline-flex items-center gap-2">
          <span className="text-[#1ebe57]">✦</span>
          <span><span className="text-[#7adc9c]">{t("announce.discount")}</span> {t("announce.over")}</span>
          <span className="hidden sm:inline text-white/40">·</span>
          <span className="hidden sm:inline text-white/70">{t("announce.shipping")}</span>
        </span>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
            <span className="inline-block w-9 h-9 relative">
              <span className="absolute inset-0 bg-gray-900"></span>
              <span className="absolute inset-[3px] bg-white"></span>
              <span className="absolute inset-[6px] bg-gray-900"></span>
            </span>
            <span className="font-display text-xl font-black tracking-tighter text-gray-900">ANAIZA<span className="text-gray-400"> · </span>SHOP</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-testid="main-nav">
            <NavLink to="/" end className={({isActive}) => `text-sm font-medium tracking-wide uppercase ${isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900'} transition-colors`}>{t("nav.home")}</NavLink>
            <NavLink to="/categorie/cutite" className={({isActive}) => `text-sm font-medium tracking-wide uppercase ${isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900'} transition-colors`} data-testid="nav-cutite">{t("nav.knives")}</NavLink>
            <NavLink to="/categorie/scule" className={({isActive}) => `text-sm font-medium tracking-wide uppercase ${isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900'} transition-colors`} data-testid="nav-scule">{t("nav.tools")}</NavLink>
            <LanguageSwitcher variant="compact" />
          </nav>

          <div className="flex items-center gap-2 md:gap-4">
            <div className="md:hidden">
              <LanguageSwitcher variant="compact" />
            </div>
            {user ? (
              <Link to={user.role === 'admin' ? '/admin' : '/cont'} className="hidden md:flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900" data-testid="account-link">
                <User size={18} strokeWidth={1.5} />
                <span className="hidden lg:inline">{user.name.split(' ')[0]}</span>
              </Link>
            ) : (
              <Link to="/login" className="hidden md:flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900" data-testid="login-link">
                <User size={18} strokeWidth={1.5} />
                <span className="hidden lg:inline">{t("nav.account")}</span>
              </Link>
            )}
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="relative flex items-center gap-2 px-4 py-2 border border-gray-300 hover:border-gray-900 transition-colors"
              data-testid="open-cart-button"
            >
              <ShoppingBag size={18} strokeWidth={1.5} />
              <span className="hidden sm:inline text-sm font-medium">{t("nav.cart")}</span>
              {count > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-gray-900 text-white text-xs font-bold" data-testid="cart-count">{count}</span>
              )}
            </button>
            <button onClick={() => setMobile(true)} className="md:hidden p-2" data-testid="open-mobile-nav">
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </header>

    {/* Mobile menu — rendered OUTSIDE header to escape its stacking context */}
    {mobile && (
      <div className="fixed inset-0 z-[80] bg-white md:hidden overflow-y-auto" data-testid="mobile-nav">
        <div className="flex items-center justify-between px-6 h-20 border-b border-gray-200">
          <span className="font-display text-xl font-black">{t("nav.menu").toUpperCase()}</span>
          <button onClick={() => setMobile(false)} className="p-2"><X size={22} /></button>
        </div>
        <nav className="flex flex-col p-6 gap-2">
          {[
            { to: "/", label: t("nav.home") },
            { to: "/categorie/cutite", label: t("nav.knives.long") },
            { to: "/categorie/scule", label: t("nav.tools.long") },
            { to: user ? (user.role === 'admin' ? "/admin" : "/cont") : "/login", label: user ? t("nav.myaccount") : t("nav.signin") },
          ].map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setMobile(false)}
              className="flex items-center justify-between border-b border-gray-200 py-4 text-lg font-medium text-gray-900">
              {l.label} <ChevronRight size={18} />
            </NavLink>
          ))}
          <div className="pt-6">
            <LanguageSwitcher variant="footer" />
          </div>
        </nav>
      </div>
    )}
    </>
  );
}
